// src/rms/data/pinService.js
// ============================================================================
// Staff PINs — signing, and unlocking the figures
// ============================================================================
// A PIN says WHO. The role says whether they may. Those are two questions and
// this file never lets the first answer the second: every server function it
// calls resolves the PIN to a member and then checks that member's role, in
// that order. See 20260820100000 for the same rule stated where it is enforced.
//
// A WRONG PIN IS A RETURN VALUE, NOT AN ERROR, on the two functions that take
// one to authorise something. That is forced rather than chosen: PostgREST runs
// an RPC in one transaction, so a function that raises rolls back everything it
// did on the way -- including the counter that would throttle the next guess.
// The previous lockout had exactly this bug and could never have counted past
// one. See 20260821140000.
//
// On the local backend there is no RLS, no second user, and nothing to protect.
// The PIN still works end to end, because the point of the local backend is to
// be able to build and test the flow — but it is presentational there, and the
// header comment is the honest version of that rather than a claim otherwise.
// ============================================================================

import { isLocalBackend } from "./index";
import { supabase } from "../config/supabaseClient";
import { createSuccessResponse, createErrorResponse } from "../utils/response";
import { capabilitiesFor, ROLES } from "./permissions";

const LOCAL_PINS = "rms.local.pins";

function readLocalPins() {
  try {
    return JSON.parse(window.localStorage.getItem(LOCAL_PINS) || "{}");
  } catch {
    return {};
  }
}

function writeLocalPins(all) {
  try {
    window.localStorage.setItem(LOCAL_PINS, JSON.stringify(all));
  } catch {
    /* storage unavailable; the PIN lasts for this page load only */
  }
}

/** The obvious ones, refused in both backends so the rule is not backend-specific. */
const TOO_OBVIOUS = new Set([
  "0000", "1111", "2222", "3333", "4444", "5555", "6666", "7777", "8888", "9999",
  "1234", "4321", "1212", "0123", "2580",
]);

export function validatePin(pin) {
  if (!/^[0-9]{4}$/.test(pin || "")) return "A PIN is four digits.";
  if (TOO_OBVIOUS.has(pin)) return "Choose a PIN that is harder to guess than that one.";
  return null;
}

export async function hasPin(operator, member) {
  if (!operator?.id) return createSuccessResponse(false);

  if (isLocalBackend) {
    return createSuccessResponse(!!readLocalPins()[member?.id]);
  }
  const { data, error } = await supabase.rpc("rms_has_pin", { p_operator_id: operator.id });
  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(!!data);
}

export async function setPin(operator, member, pin) {
  const problem = validatePin(pin);
  if (problem) return createErrorResponse(problem);
  if (!operator?.id) return createErrorResponse("No business loaded.");

  if (isLocalBackend) {
    if (!member?.id) return createErrorResponse("You are not an active member of this business.");
    // Stored in the clear, deliberately and only here. There is no RLS on this
    // backend and no second user, so hashing it would be a costume rather than
    // a control -- and pretending otherwise is how somebody later assumes the
    // local store is safe to put real secrets in.
    writeLocalPins({ ...readLocalPins(), [member.id]: pin });
    return createSuccessResponse(true);
  }

  const { error } = await supabase.rpc("rms_set_my_pin", {
    p_operator_id: operator.id,
    p_pin: pin,
  });
  return error ? createErrorResponse(error.message) : createSuccessResponse(true);
}

/**
 * Signs one action and returns the signature id.
 *
 * The id is then written onto the row being signed, where a trigger checks it
 * was minted moments ago, for this operator, for this action, and has not been
 * spent. That is what stops a client signing and not writing, or writing and
 * not signing.
 */
export async function signAction(operator, { pin, action, subjectTable, subjectId, context }) {
  if (!operator?.id) return createErrorResponse("No business loaded.");
  if (!/^[0-9]{4}$/.test(pin || "")) return createErrorResponse("That PIN was not recognised.");

  if (isLocalBackend) {
    const pins = readLocalPins();
    const memberId = Object.keys(pins).find((id) => pins[id] === pin);
    if (!memberId) return createErrorResponse("That PIN was not recognised.");

    const members = (await import("./index")).default;
    const member = (await members.get("members", memberId)).data;
    // operator_id as well as status. The local PIN store is one flat map in
    // localStorage with no tenant column, so it keeps entries belonging to
    // businesses that have been deleted or switched away from -- and without
    // this line one of those stale PINs resolves to a member of a DIFFERENT
    // operator. The SQL path has never had the hole: rms_resolve_pin joins on
    // m.operator_id = p_operator_id.
    if (!member || member.status !== "active" || member.operator_id !== operator.id) {
      return createErrorResponse("That PIN was not recognised.");
    }
    if (!allowed(member.role, action)) {
      return createErrorResponse("Your access does not cover that.");
    }

    const created = await members.create("staffActions", {
      operator_id: operator.id,
      member_id: member.id,
      member_email: member.email,
      action,
      subject_table: subjectTable || null,
      subject_id: subjectId || null,
      context: context || {},
    });
    if (!created.success) return created;
    // `member` is additive -- every existing caller reads `.data` and is
    // unaffected. The Supabase path has no equivalent and needs none: there the
    // name is resolved by the trigger, from the PIN, server-side.
    return { ...createSuccessResponse(created.data.id), member };
  }

  const { data, error } = await supabase.rpc("rms_sign_action", {
    p_operator_id: operator.id,
    p_pin: pin,
    p_action: action,
    p_subject_table: subjectTable || null,
    p_subject_id: subjectId || null,
    p_context: context || {},
  });
  // A raised error still means something structural -- not a member, throttled.
  // A WRONG PIN comes back as ok:false in the body, because a function that
  // raises cannot record the failure that would throttle the next guess: the
  // abort rolls the counter back with it. See 20260821140000.
  if (error) return createErrorResponse(error.message);
  if (!data?.ok) return createErrorResponse(data?.error || "That PIN was not recognised.");
  return createSuccessResponse(data.signature_id);
}

/**
 * Mirrors rms_action_allowed. Two encodings of one decision; review together.
 *
 * The final `false` matches the SQL's ELSE: an action nobody has classified is
 * refused rather than allowed, so adding a class is a deliberate act in both
 * places or in neither.
 */
function allowed(role, action) {
  const can = capabilitiesFor(role);
  if (action.startsWith("agreement.") || action.startsWith("record.")) return can.ops;
  if (action.startsWith("money.") || action.startsWith("revenue.")) return can.money;
  if (action.startsWith("billing.")) return can.viewBilling;
  if (action.startsWith("asset.")) return can.addAsset;
  if (action.startsWith("pricing.")) return can.managePricing;
  if (action.startsWith("settings.")) return can.openSettings;
  // Checked with settings in the SQL and checked beside it here. Note this is
  // reached at all only because "agreement_template.save" does NOT start with
  // "agreement." -- an underscore where that prefix wants a dot. The SQL relies
  // on the same distinction and spells it out, because getting it wrong would
  // hand the contract to every staff member on the counter.
  if (action.startsWith("agreement_template.")) return can.manageAgreements;
  // Every active role. Blocking a vehicle out, taking a booking and taking a
  // payment are all the counter's job; the signature answers "who", not
  // "whether". A refund is a negative payment and signs the same way -- what
  // stops a staff member issuing one is the RESTRICTIVE policy on rms_payments,
  // so the rule about reversing money lives in exactly one place.
  if (
    action.startsWith("block.") ||
    action.startsWith("reservation.") ||
    action.startsWith("payment.") ||
    // Sending a contract out to a customer. Grouped here, not with
    // agreement_template above: writing the words is the owner's, using them is
    // the counter's. And note again that this is reached only because
    // "agreement_doc.send" does not start with "agreement." -- underscore, not
    // dot.
    action.startsWith("agreement_doc.")
  ) {
    return ROLES.includes(role);
  }
  return false;
}

/**
 * Opens a gated section on somebody's PIN, and says who they turned out to be.
 *
 * This is the piece a shared counter terminal needs. The terminal signs in as a
 * staff-level member, so its SESSION role is the wrong thing to ask when an
 * owner walks up wanting the rate card -- the question is whose PIN was typed.
 *
 * It is not a login and it grants nothing. RLS is unchanged and still answers
 * to auth.uid(); what comes back is an identity for the screen to render for.
 * Any write behind that screen still has to be permitted on its own terms.
 */
export async function elevate(operator, pin, section) {
  if (!operator?.id) return createErrorResponse("No business loaded.");
  if (!/^[0-9]{4}$/.test(pin || "")) return createErrorResponse("That PIN was not recognised.");

  if (isLocalBackend) {
    const pins = readLocalPins();
    const memberId = Object.keys(pins).find((id) => pins[id] === pin);
    if (!memberId) return createErrorResponse("That PIN was not recognised.");

    const dbi = (await import("./index")).default;
    const who = (await dbi.get("members", memberId)).data;
    // Scoped to this business -- see the note in signAction.
    if (!who || who.status !== "active" || who.operator_id !== operator.id) {
      return createErrorResponse("That PIN was not recognised.");
    }
    if (!allowed(who.role, `${section}.open`)) {
      return createErrorResponse("Your access does not cover that.");
    }

    // Logged here too. "Did opening the accounts leave a trace" is exactly the
    // behaviour worth being able to exercise without a backend.
    await dbi.create("staffActions", {
      operator_id: operator.id,
      member_id: memberId,
      member_email: who.email || null,
      action: `${section}.open`,
      context: { section },
    });

    return createSuccessResponse({
      member_id: memberId,
      role: who.role,
      first_name:
        (who.display_name || "").trim().split(/\s+/)[0] ||
        (who.email || "").split("@")[0] ||
        null,
    });
  }

  const { data, error } = await supabase.rpc("rms_elevate", {
    p_operator_id: operator.id,
    p_pin: pin,
    p_section: section,
  });
  if (error) return createErrorResponse(error.message);
  if (!data?.ok) return createErrorResponse(data?.error || "That PIN was not recognised.");
  return createSuccessResponse(data);
}

/**
 * The figures, fetched on a PIN rather than hidden behind one.
 *
 * There is nothing to reveal client-side: the totals are computed on the server
 * and returned only to somebody who has just proved they may see them. A modal
 * over numbers already in the page would be a curtain.
 */
export async function revenueSummary(operator, pin, member) {
  if (!operator?.id) return createErrorResponse("No business loaded.");

  if (isLocalBackend) {
    const pins = readLocalPins();
    const memberId = Object.keys(pins).find((id) => pins[id] === pin);
    if (!memberId) return createErrorResponse("That PIN was not recognised.");
    // The PIN has to belong to somebody in THIS business, not merely to
    // somebody. Same stale-entry problem as signAction.
    if (member?.id && memberId !== member.id && member.operator_id !== operator.id) {
      return createErrorResponse("That PIN was not recognised.");
    }
    if (!capabilitiesFor(member?.role).money) {
      return createErrorResponse("Your access does not cover the figures.");
    }

    // Computed in the browser here because there is no server to compute it in.
    // The Supabase path does this in SQL, which is the half that matters.
    const dbi = (await import("./index")).default;
    const invoices = (await dbi.list("invoices", { operator_id: operator.id })).data || [];
    const subs = (await dbi.list("subscriptions", { operator_id: operator.id })).data || [];
    const payments = (await dbi.list("payments", { operator_id: operator.id })).data || [];

    const unpaid = invoices.filter((i) =>
      ["sent", "partially_paid", "overdue"].includes(i.status)
    );
    const active = subs.filter((s) => ["active", "past_due"].includes(s.status));
    const since = Date.now() - 30 * 864e5;

    // Logged here too, not only in the SQL path. The point of the local
    // backend is that the flow can be exercised, and "did looking at the
    // figures leave a trace" is exactly the thing worth exercising.
    const who = (await dbi.get("members", memberId)).data;
    await dbi.create("staffActions", {
      operator_id: operator.id,
      member_id: memberId,
      member_email: who?.email || null,
      action: "revenue.view",
      context: {},
    });

    return createSuccessResponse({
      outstanding_cents: unpaid.reduce((n, i) => n + (i.total_cents - i.amount_paid_cents), 0),
      unpaid_invoices: unpaid.length,
      monthly_recurring_cents: active.reduce((n, s) => n + s.amount_cents, 0),
      active_leases: active.length,
      collected_30d_cents: payments
        .filter((p) => p.status === "captured" && new Date(p.created_at).getTime() >= since)
        .reduce((n, p) => n + p.amount_cents, 0),
      as_of: new Date().toISOString(),
    });
  }

  const { data, error } = await supabase.rpc("rms_revenue_summary", {
    p_operator_id: operator.id,
    p_pin: pin,
  });
  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(data);
}

/**
 * Clears somebody's PIN, so they can choose a new one.
 *
 * Owner only, and deliberately CLEAR rather than SET: nobody should be able to
 * choose another person's signature. The member picks a new one the next time
 * something asks, exactly as they did the first time.
 *
 * This is also the only way out of a lockout. rms_resolve_pin counts a wrong
 * guess against every unlocked PIN in the business -- it cannot know whose was
 * meant -- so five wrong attempts at a shared counter lock the whole team for
 * fifteen minutes. Without this there is no way to intervene.
 */
export async function clearPin(operator, member) {
  if (!member?.id) return createErrorResponse("No member selected.");

  if (isLocalBackend) {
    const all = readLocalPins();
    delete all[member.id];
    writeLocalPins(all);
    return createSuccessResponse(true);
  }

  const { error } = await supabase.rpc("rms_clear_member_pin", { p_member_id: member.id });
  return error ? createErrorResponse(error.message) : createSuccessResponse(true);
}

/**
 * Forget my own PIN, so I can choose another.
 *
 * rms_clear_member_pin needs no PIN of its own -- it was written that way on
 * purpose, and its comment says why: "requiring a PIN to clear a PIN would
 * strand an owner who had forgotten their own". That escape hatch existed and
 * had become unreachable, because the only screen calling it was Settings, and
 * Settings now opens on a PIN.
 *
 * It is still owner-only at the database, which is the right shape: an owner
 * can recover themselves, and everybody else asks the person who can already
 * clear theirs.
 */
export async function clearMyPin(operator, member) {
  if (!member?.id) return createErrorResponse("No member loaded.");

  if (isLocalBackend) {
    const all = readLocalPins();
    delete all[member.id];
    writeLocalPins(all);
    return createSuccessResponse(true);
  }

  const { error } = await supabase.rpc("rms_clear_member_pin", { p_member_id: member.id });
  return error ? createErrorResponse(error.message) : createSuccessResponse(true);
}

/** Who has a PIN, and who is currently locked out. Booleans only. */
export async function pinStatus(operator) {
  if (!operator?.id) return createSuccessResponse({});

  if (isLocalBackend) {
    const pins = readLocalPins();
    return createSuccessResponse(
      Object.keys(pins).reduce((acc, id) => ({ ...acc, [id]: { has_pin: true, locked: false } }), {})
    );
  }

  const { data, error } = await supabase.rpc("rms_members_with_pins", {
    p_operator_id: operator.id,
  });
  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(
    (data || []).reduce(
      (acc, r) => ({ ...acc, [r.member_id]: { has_pin: r.has_pin, locked: r.locked } }),
      {}
    )
  );
}

export default {
  validatePin, hasPin, setPin, clearPin, clearMyPin, pinStatus, signAction, elevate, revenueSummary,
};
